import { Component, Input } from '@angular/core';
import { MagicLinkModel } from '@shared/models/platform-tool-models';
import { PlatformCommand, PlatformCommandResponse } from '@shared/commands/command-base';
import { InvalidateSubject } from '@shared/helpers/invalidate.subject';
import { CommandService } from '@core/services/command.service';
import { take } from 'rxjs/operators';

/**
 * WS-E-11-04. Shown on the Workspace Details page when initialization failed (Status = "Failed", KI-19),
 * in place of the endless "Initiating Workspace" spinner. All copy is backend-driven -- the API surfaces
 * the title, the step that failed and why via the platform link's fieldValues -- so this component holds no
 * hardcoded message. Rendered by the shared platform-tool-banner via BannerComponent, the same mechanism
 * the Filing Manager submission-status banner uses.
 *
 * WS-E-11-06 (US-3). When the backend supplies a "RetryCommand" (an InitializeWorkspaceCommand, the same
 * command the create flow fires), the banner offers a Retry button. The init steps are now idempotent and
 * the InitializeWorkspaceCommandConsumer guard admits a restart from "Failed", so dispatching it re-runs the
 * saga without creating duplicate companies/projects/users. Dispatched via CommandService like the Filing
 * Manager submission-status banner's backend commands.
 */
@Component({
  standalone: false,
  selector: 'workspace-failure-banner',
  templateUrl: './workspace-failure-banner.component.html',
  styleUrls: ['./workspace-failure-banner.component.scss']
})
export class WorkspaceFailureBannerComponent {
  @Input() magicLinkModel!: MagicLinkModel;
  @Input() handler: any;
  @Input() invalidateSubject!: InvalidateSubject;

  /** Guards against a double-click firing a second init while the first is still in flight. */
  isRetrying = false;

  constructor(private commandService: CommandService) {}

  get bannerTitle(): string {
    return (this.magicLinkModel?.fieldValues?.['BannerTitle'] as string) || 'Workspace initialization failed';
  }

  get failedStep(): string {
    return (this.magicLinkModel?.fieldValues?.['FailedStep'] as string) || '';
  }

  get failedReason(): string {
    return (this.magicLinkModel?.fieldValues?.['FailedReason'] as string) || '';
  }

  get retryCommand(): PlatformCommand | undefined {
    return this.magicLinkModel?.fieldValues?.['RetryCommand'] as PlatformCommand;
  }

  get canRetry(): boolean {
    return !!this.retryCommand;
  }

  get retryLabel(): string {
    return (this.retryCommand?.title as string) || 'Retry';
  }

  retry(): void {
    const command = this.retryCommand;
    if (!command || this.isRetrying) {
      return;
    }
    this.isRetrying = true;
    this.commandService.executePlatformCommand(command).pipe(take(1)).subscribe(
      (response: PlatformCommandResponse) => {
        this.isRetrying = false;
        this.commandService.handleResponse(response);
      },
      (error: unknown) => {
        this.isRetrying = false;
        console.error('Workspace retry command failed', error);
      }
    );
  }
}
